import AppRouteContent from './catalog/components/AppRouteContent'
import ShopPageShell from './catalog/components/ShopPageShell'
import { useShopAppRuntime } from './catalog/hooks/useShopAppRuntime'
import type { AppProps } from './catalog/types'
import { buildShopSeo } from './catalog/seo'
import { buildAppEffectiveSeo } from './catalog/appSeo'
import { resolveCustomerRouteMode } from './customer-auth'
import { resolveRuntimeFaviconUrl, resolveRuntimeSiteName } from './catalog/runtimeSettings'
import { useShopI18n } from './i18n'
import CookieConsentBanner from './components/CookieConsentBanner'

const resolveSiteUrl = (incoming: string | undefined) => {
  if (incoming && incoming.trim()) return incoming.trim().replace(/\/+$/, '')
  if (typeof window !== 'undefined') return window.location.origin
  return 'http://localhost:3000'
}

function App({
  initialPathname = '/',
  initialSearch = '',
  initialLocale,
  initialSeo,
  siteUrl,
  initialSiteName,
  initialFaviconUrl,
}: AppProps) {
  const { locale, language } = useShopI18n()
  const runtime = useShopAppRuntime({ initialPathname, initialSearch, initialLocale })
  const resolvedSiteUrl = resolveSiteUrl(siteUrl)
  const runtimeSiteName = resolveRuntimeSiteName(runtime.homeSettings, initialSiteName || 'Ecommerce')
  const runtimeFaviconUrl = resolveRuntimeFaviconUrl(runtime.homeSettings, initialFaviconUrl || '/favicon.ico')
  const shouldRenderRuntimeFooter = Boolean(
    runtime.homeSettings.footerNote ||
      runtime.homeSettings.footerLinks.length ||
      runtime.homeSettings.socialLinks.length ||
      runtime.homeSettings.contactEmail ||
      runtime.homeSettings.contactPhone,
  )
  const isCartRoute = runtime.route.mode === 'page' && runtime.route.slug.trim().toLowerCase() === 'cart'
  const customerRouteMode = resolveCustomerRouteMode(runtime.route)
  const seo = buildShopSeo({
    route: runtime.route,
    pageData: runtime.pageData,
    productData: runtime.productData,
    categoryRouteData: runtime.categoryRouteData,
    homePageData: runtime.homePageData,
    currentPathname: typeof window !== 'undefined' ? window.location.pathname : initialPathname,
    siteName: runtimeSiteName,
    activeLocale: locale,
  })
  const hasRuntimeSeoData = Boolean(
    runtime.pageData ||
      runtime.productData ||
      runtime.categoryRouteData ||
      runtime.homePageData ||
      runtime.activeError,
  )
  const effectiveSeo = buildAppEffectiveSeo({
    language,
    runtimeSiteName,
    customerRouteMode,
    isCartRoute,
    hasRuntimeSeoData,
    seo,
    initialSeo,
  })

  return (
    <ShopPageShell
      seo={effectiveSeo}
      siteUrl={resolvedSiteUrl}
      siteName={runtimeSiteName}
      faviconUrl={runtimeFaviconUrl}
      locale={locale}
    >
      <AppRouteContent
        runtime={runtime}
        customerRouteMode={customerRouteMode}
        shouldRenderRuntimeFooter={shouldRenderRuntimeFooter}
      />
      <CookieConsentBanner cookiePolicyPath={runtime.homeSettings.cookiePolicyPath} />
    </ShopPageShell>
  )
}

export default App
